/**
 * UpgradeModal — in-app upgrade surface.
 *
 * Opens IN PLACE (Dialog on desktop, bottom Drawer on mobile) instead of
 * navigating away to the /pricing screen. Plans/add-ons are presented as a
 * focused, swipeable carousel (AddonsPricing forceSlider) so the user can
 * compare options without losing their place in the app.
 *
 * No decorative header — the billing tabs (Mensal/Anual) sit at the very top
 * of the drawer/dialog, with a floating close (X) button that never gets
 * covered by them.
 */

import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { AddonsPricing, type AddonId } from './addons-pricing';
import { notify } from '../lib/toast';

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpgradeModal({ open, onOpenChange }: UpgradeModalProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Clicking a plan must NOT instantly activate it — there is no real payment
  // yet. Until Asaas checkout (PIX / boleto / cartão) is wired, just inform.
  const handleSubscribe = (addonId: AddonId) => {
    if (addonId === 'free') return;
    notify.info(
      t('upgradeModal.checkoutSoonTitle', 'Checkout em breve'),
      t('upgradeModal.checkoutSoonDescription', 'O pagamento (PIX, boleto e cartão via Asaas) está sendo finalizado. Em breve você poderá assinar.'),
    );
  };

  // min-h-0 lets this grow with content and ONLY scroll once the dialog is
  // capped at the screen height — so the modal is content-sized when it fits.
  // pt-4 clears the floating close button before the billing tabs start.
  const Body = (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-4 md:px-6">
      <AddonsPricing variant="app" forceSlider onSubscribe={handleSubscribe} />
    </div>
  );

  // ── Mobile: bottom drawer ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
        {/* NOTE: do NOT add `relative` here — it overrides vaul's `fixed`
            positioning and drops the drawer out of view. The floating X below
            still anchors correctly to this fixed drawer. */}
        <DrawerContent
          className={cn('flex max-h-[90dvh] flex-col border-border bg-card text-card-foreground')}
        >
          <DrawerTitle className="sr-only">{t('upgradeModal.plansAndAddons', 'Planos e add-ons')}</DrawerTitle>
          {/* Drawer has no built-in close button — float our own above the
              tabs so it's always reachable and never overlapped. */}
          <button
            onClick={() => onOpenChange(false)}
            aria-label={t('upgradeModal.close', 'Fechar')}
            className="absolute right-3 top-3 z-20 grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border bg-card/80 text-muted-foreground backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground"
          >
            <X size={16} />
          </button>
          {Body}
        </DrawerContent>
      </Drawer>
    );
  }

  // ── Desktop: centered dialog ────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex max-h-[90vh] flex-col gap-0 overflow-hidden border-border bg-card p-0 text-card-foreground',
          'sm:max-w-3xl',
        )}
      >
        <DialogTitle className="sr-only">{t('upgradeModal.plansAndAddons', 'Planos e add-ons')}</DialogTitle>
        {Body}
      </DialogContent>
    </Dialog>
  );
}
